<script setup lang="ts">
import { ITEMS_PER_PAGE } from "~/common/constants/items-per-page";
import type { UpdateSearch } from "~/common/interfaces/update-search.interface";

const props = defineProps({
	total: {
		type: Number,
		default: 0,
		required: true
	}
});
const emit = defineEmits<{(event: "update:search", updateSearch: UpdateSearch): void}>();

const applyShowMore = ref(false);

const route = useRoute();
const router = useRouter();

const { isMobile } = useDevice();
const { next, prev, currentPage, pageCount, isFirstPage, isLastPage } = useOffsetPagination({
	total: () => props.total,
	page: parseInt(route.query.page?.toString() || "1"),
	pageSize: ITEMS_PER_PAGE,
	onPageChange () {
		setQuery(currentPage.value, applyShowMore.value ? "" : "#gallery");

		emit("update:search", {
			page: currentPage.value,
			showMore: applyShowMore.value
		});
		applyShowMore.value = false;
	}
});

const paginationButtons = computed(() => {
	const adjacentPages = isMobile ? [currentPage.value - 1, currentPage.value, currentPage.value + 1] : [currentPage.value - 2, currentPage.value - 1, currentPage.value, currentPage.value + 1, currentPage.value + 2];
	const validAdjacentPages = adjacentPages.filter(page => page >= 1 && page <= pageCount.value);
	validAdjacentPages.unshift(1);
	validAdjacentPages.push(pageCount.value);
	const pagesWithoutDuplicates = [...new Set(validAdjacentPages)];

	const finishedPages = pagesWithoutDuplicates.reduce<{name: string, page: number}[]>((acc, value) => {
		const prev = acc.at(-1);
		if (prev) {
			const difference = value - prev.page;
			if (difference > 1) {
				const median = value - Math.round(difference / 2);
				acc.push(
					{ name: "...", page: median }
				);
			}
		}

		acc.push(
			{ name: value.toString(), page: value }
		);
		return acc;
	}, []);

	return finishedPages;
});

const getQuery = (page: number) => {
	return {
		...route.query,
		page
	};
};
const setQuery = (page: number, hash: string) => {
	const query = getQuery(page);
	router.push({
		hash,
		query
	});
};

const showMore = () => {
	applyShowMore.value = true;
	next();
};
</script>

<template>
	<div class="font-medium text-lg">
		<div
			v-if="!isLastPage"
			class="flex justify-center items-center py-4 border-y border-gray"
		>
			<button
				:title="$t('showMore')"
				class="group flex items-center gap-x-3"
				@click="showMore"
			>
				<Icon
					name="teenyicons:refresh-solid"
					size="20"
				/>
				<span
					class="uppercase border-b-2 leading-[22px] border-b-transparent"
					:class="'group-hover:border-b-black'"
				>
					{{ $t('showMore') }}
				</span>
			</button>
		</div>
		<div class="flex items-center justify-between mt-5">
			<NuxtLink
				:title="$t('previousPage')"
				class="group flex items-center gap-x-2 uppercase leading-[22px] cursor-pointer select-none"
				:class="isFirstPage && 'text-gray !cursor-default'"
				:to="isFirstPage ? {query: getQuery(currentPage)} : {query: getQuery(currentPage - 1), hash: '#gallery'}"
				@click="prev"
			>
				<Icon
					name="teenyicons:left-solid"
					size="20"
				/>
				<span
					class="border-b-2 border-b-transparent sm:hidden xs:hidden"
					:class="isFirstPage || 'group-hover:border-b-black'"
				>
					{{ $t('previous') }}
				</span>
			</NuxtLink>
			<div class="flex gap-x-2 xs:gap-x-1">
				<AppButton
					v-for="({page, name}) in paginationButtons"
					:key="page"
					:title="page"
					class="w-7 h-7 !p-0 xs:w-6 xs:h-6 xs:text-sm"
					:class="currentPage === page && 'cursor-default'"
					:fill="currentPage === page"
					@click="currentPage = page"
				>
					<NuxtLink
						class="block w-full h-full"
						:to="currentPage === page ? {query: getQuery(page)} : {query: getQuery(page), hash: '#gallery'}"
						:class="currentPage === page && 'cursor-default'"
					>
						{{ name }}
					</NuxtLink>
				</AppButton>
			</div>
			<NuxtLink
				:title="$t('nextPage')"
				class="group flex items-center gap-x-2 uppercase leading-[22px] cursor-pointer select-none"
				:class="isLastPage && 'text-gray !cursor-default'"
				:to="isLastPage ? undefined : {query: getQuery(currentPage + 1), hash: '#gallery'}"
				@click="next"
			>
				<span
					class="border-b-2 border-b-transparent sm:hidden xs:hidden"
					:class="isLastPage || 'group-hover:border-b-black'"
				>
					{{ $t('next') }}
				</span>
				<Icon
					name="teenyicons:right-solid"
					size="20"
				/>
			</NuxtLink>
		</div>
	</div>
</template>
